import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomButton from "../../pages/CustomButton";
import { getAuthHeaders } from "../../services/useAuth";
import { BASE_URL } from "../../config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const ProductForm = () => {
  const [searchParams] = useSearchParams();
  const editProductId = searchParams.get("edit");
  const navigate = useNavigate();

  const initialFormData = {
    name: "",
    product_type_id: "",
    brand_name: "",
    short_name: "",
    visible_in_dcr: true,
    hsn_code: "",
    mapping_code: "",
    composition: "",
    dosage: "",
    packaging: "",
    strip_box: "",
    case_pack: "",
    mrp: 0,
    product_class_id: null,
    product_url: "",
    is_active: true,
    division_ids: [],
    c_slot: 0,
    unit: "",
    s_rate: 0,
    r_rate: 0,
    gst_rate: 0,
    ptd: 0,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [productTypes, setProductTypes] = useState([]);
  const [productClasses, setProductClasses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Modal form states
  const [fromDate, setFromDate] = useState("");
  const [file, setFile] = useState(null);

  // Upload status
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError] = useState("");

  const openUploadModal = () => setUploadModalOpen(true);
  const closeUploadModal = () => setUploadModalOpen(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!fromDate || !file) {
      setError("Please select both date and file");
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("from_date", fromDate);

      const response = await axios.post(
        `${BASE_URL}/api/product/upload`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("File uploaded successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      setUploadStatus("Upload successful!");
      setError("");
      console.log("API response:", response.data);

      setFromDate("");
      setFile(null);
      setUploadModalOpen(false);
    } catch (err) {
      console.error("Upload error:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Upload failed",
      );
      setUploadStatus("");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [typesResponse, classesResponse, divisionsResponse] =
          await Promise.all([
            axios.get(`${BASE_URL}/api/product/product-types`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/product/product-class`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/divisions`, {
              headers: getAuthHeaders(),
            }),
          ]);

        setProductTypes(typesResponse.data);
        setProductClasses(classesResponse.data);
        setDivisions(divisionsResponse.data);

        if (editProductId) {
          await fetchProductData(editProductId);
        }

        setIsLoading(false);
      } catch (err) {
        setError("Failed to fetch form data");
        setIsLoading(false);
      }
    };

    fetchData();
  }, [editProductId]);

  const fetchProductData = async (productId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/product/?id=${productId}`,
        { headers: getAuthHeaders() },
      );

      const product = response.data[0];
      if (!product) {
        setError("Product not found");
        return;
      }

      // Extract price details if available
      const priceDetails =
        Array.isArray(product.prices) && product.prices.length > 0
          ? product.prices[0]
          : {};

      setFormData({
        name: product.name || "",
        product_type_id:
          product.product_type_id?.toString() ||
          product.product_type?.id?.toString() ||
          "",
        product_class_id:
          product.product_class_id?.toString() ||
          product.product_class?.id?.toString() ||
          "",
        division_ids: (product.division_ids || []).map((d) =>
          Number(d.id || d),
        ),
        brand_name: product.brand_name || "",
        short_name: product.short_name || "",
        visible_in_dcr: product.visible_in_dcr ?? true,
        hsn_code: product.hsn_code || "",
        mapping_code: product.mapping_code || "",
        composition: product.composition || "",
        dosage: product.dosage || "",
        packaging: product.packaging || "0.00",
        strip_box: product.strip_box || "",
        case_pack: product.case_pack || "",
        mrp: priceDetails.mrp || product.mrp || "0.00",
        product_url: product.product_url || "https://",
        is_active: product.is_active ?? true,
        c_slot: priceDetails.c_slot || "0.00",
        unit: priceDetails.unit || product.unit || "",
        s_rate: priceDetails.s_rate || "0.00",
        r_rate: priceDetails.r_rate || "0.00",
        gst_rate: priceDetails.gst_rate || "0.00",
        ptd: priceDetails.ptd || "0.00",
      });
    } catch (err) {
      setError("Failed to fetch product data");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name.startsWith("division-")) {
      const divisionId = parseInt(name.replace("division-", ""));
      setFormData((prev) => {
        const divisionIds = checked
          ? [...prev.division_ids, divisionId]
          : prev.division_ids.filter((id) => id !== divisionId);

        return { ...prev, division_ids: divisionIds };
      });
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    setIsSubmitting(true);
    setError(null);

    const payload = {
      ...formData,
      product_class_id:
        formData.product_class_id === "" || formData.product_class_id === null
          ? null
          : Number(formData.product_class_id),
    };

    if (editProductId) {
      await axios.put(`${BASE_URL}/api/product/${editProductId}`, payload, {  // ← payload, not formData
        headers: getAuthHeaders(),
      });
      toast.success("Product updated successfully!");
      setFormData(initialFormData);
      navigate("/admin/add-product");
    } else {
      await axios.post(`${BASE_URL}/api/product/`, payload, {  // ← payload, not formData
        headers: getAuthHeaders(),
      });
      toast.success("Product created successfully!");
      setFormData(initialFormData);
      navigate("/admin/add-product");
    }
  } catch (err) {
      const errorMessage = editProductId
        ? "Failed to update product"
        : "Failed to create product";

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = () => {
    navigate("/admin/edit-product");
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 ">
        <div className="p-3">Loading form data...</div>
      </div>
    );
  }

  return (
    <div className="">
      <div>
        {/* Button to open modal */}
        <div className="flex justify-end mb-2">
          <CustomButton
            onClick={openUploadModal}
            label="Upload"
            variant="add"
          />
        </div>

        {/* Upload Modal */}
        {uploadModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-4xl p-4 overflow-y-auto max-h-screen">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Upload Product File</h2>
                <button
                  onClick={closeUploadModal}
                  className="text-gray-600 hover:text-gray-800 text-lg font-bold"
                >
                  &times;
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      File (CSV/Excel)
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                  </div>

                  <div className="pt-2">
                    <CustomButton
                      onClick={handleFileUpload}
                      label="Upload"
                      variant="go"
                      fullWidth
                    />
                  </div>

                  {uploadStatus && (
                    <div
                      className={`mt-2 p-2 rounded text-center ${
                        uploadStatus.includes("success")
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {uploadStatus}
                    </div>
                  )}

                  {error && (
                    <div className="mt-2 p-2 rounded bg-red-100 text-red-700 text-center">
                      {error}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">
                    File Requirements
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                    <li>CSV or Excel file format</li>
                    <li>
                      Must contain columns: Product Name, Brand Name, Category
                    </li>
                    <li>Optional columns: Division, GST, S_RATE,</li>
                    <li>Date format: DD-MM-YYYY or YYYY-MM-DD</li>
                    <li>Target values should be numeric</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden">
        <div className="bg-green-100 px-4 py-2 border-b border-gray-300">
          <h1 className="text-lg font-bold text-center">
            {editProductId ? "Edit Product Details" : "Product Details"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            {/* Row 1: PRODUCT and PRODUCT TYPE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  PRODUCT <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  PRODUCT TYPE <span className="text-red-600">*</span>
                </label>
                <select
                  name="product_type_id"
                  value={formData.product_type_id}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select</option>
                  {productTypes.map((cls) => (
                    <option key={cls.id} value={cls.id.toString()}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 5: DIVISION */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">
                  DIVISION <span className="text-red-600">*</span>
                </label>

                <div className="grid grid-cols-3 gap-2">
                  {divisions.map((division) => (
                    <label
                      key={division.id}
                      className="flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        name={`division-${division.id}`}
                        checked={formData.division_ids.includes(
                          Number(division.id),
                        )}
                        onChange={handleChange}
                        className="w-4 h-4 scale-125 accent-blue-600"
                      />
                      <span>{division.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">M.R.P</label>
                <input
                  type="text"
                  name="mrp"
                  value={formData.mrp}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border rounded-md text-right focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
              <label className="block text-sm font-medium mb-1">CLASS</label>

            <select
              name="product_class_id"
              value={formData.product_class_id || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  product_class_id: e.target.value || null,
                }))
              }
              className="w-full p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select</option>

              {productClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  C/S LOT
                </label>
                <input
                  type="text"
                  name="c_slot"
                  value={formData.c_slot}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">UNIT</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Stockist Price
                </label>
                <input
                  type="number"
                  name="s_rate"
                  value={formData.s_rate}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Retailer Price
                </label>
                <input
                  type="number"
                  name="r_rate"
                  value={formData.r_rate}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Distributor Price
                </label>
                <input
                  type="number"
                  name="ptd"
                  value={formData.ptd}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  PACKAGING
                </label>
                <input
                  type="text"
                  name="packaging"
                  value={formData.packaging}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  GST RATE
                </label>
                <input
                  type="number"
                  name="gst_rate"
                  value={formData.gst_rate}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
            </div>

            {/* Row 2: BRAND NAME, SHORT NAME, VISIBLE IN DCR, HSN CODE, MAPPING CODE */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  BRAND NAME
                </label>
                <input
                  type="text"
                  name="brand_name"
                  value={formData.brand_name}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  SHORT NAME
                </label>
                <input
                  type="text"
                  name="short_name"
                  value={formData.short_name}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col justify-end">
                <label className="block text-sm font-medium mb-1">
                  VISIBLE IN DCR
                </label>
                <div className="flex items-center h-8">
                  <input
                    type="checkbox"
                    name="visible_in_dcr"
                    checked={formData.visible_in_dcr}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  HSN CODE
                </label>
                <input
                  type="text"
                  name="hsn_code"
                  value={formData.hsn_code}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  MAPPING CODE
                </label>
                <input
                  type="text"
                  name="mapping_code"
                  value={formData.mapping_code}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Row 3: COMPOSITION and DOSAGE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  COMPOSITION
                </label>
                <textarea
                  name="composition"
                  value={formData.composition}
                  onChange={handleChange}
                  rows={3}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">DOSAGE</label>
                <textarea
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleChange}
                  rows={3}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            {/* Row 4: STRIP/BOX and CASE PACK */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  STRIP/BOX
                </label>
                <input
                  type="text"
                  name="strip_box"
                  value={formData.strip_box}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  CASE PACK
                </label>
                <input
                  type="text"
                  name="case_pack"
                  value={formData.case_pack}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                />
              </div>
            </div>

            {/* Row 6: ACTIVE and PRODUCT URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-4">
                <label className="block text-sm font-medium">ACTIVE</label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="is_active"
                      value="true"
                      checked={formData.is_active === true}
                      onChange={handleChange}
                      className="mr-2 w-4 h-4"
                    />
                    <span className="text-sm">ACTIVE</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="is_active"
                      value="false"
                      checked={formData.is_active === false}
                      onChange={handleChange}
                      className="mr-2 w-4 h-4"
                    />
                    <span className="text-sm">DE ACTIVE</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  PRODUCT URL
                </label>
                <input
                  type="text"
                  name="product_url"
                  value={formData.product_url}
                  onChange={handleChange}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center space-x-4 pt-6">
              <CustomButton
                type="submit"
                variant="save"
                size="md"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : editProductId
                    ? "Update"
                    : "Submit"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="cancel"
                size="md"
                onClick={handleReturn}
              >
                Return
              </CustomButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;